import { useTheme, type ThemeMode } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { Monitor, Moon, Sun } from "lucide-react";

const OPTIONS: { value: ThemeMode; label: string; hint: string; Icon: typeof Sun }[] = [
  { value: "system", label: "System", hint: "Follow my device", Icon: Monitor },
  { value: "light", label: "Light", hint: "Always white", Icon: Sun },
  { value: "dark", label: "Dark", hint: "Charcoal", Icon: Moon },
];

export function ThemeSelector() {
  const { mode, resolved, setMode } = useTheme();

  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Theme">
        {OPTIONS.map(({ value, label, hint, Icon }) => {
          const active = mode === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setMode(value)}
              className={cn(
                "flex items-center gap-3 rounded-xl border p-3 text-left transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active
                  ? "border-primary bg-accent text-accent-foreground"
                  : "border-border bg-surface text-foreground hover:bg-accent/60",
              )}
            >
              <span
                className={cn(
                  "grid h-9 w-9 shrink-0 place-items-center rounded-lg border",
                  active ? "border-primary/40 bg-primary/15 text-primary" : "border-border text-muted-foreground",
                )}
              >
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium">{label}</span>
                <span className="block truncate text-xs text-muted-foreground">{hint}</span>
              </span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground">
        {mode === "system"
          ? `Following your device — currently ${resolved}.`
          : `Always ${mode}, regardless of your device setting.`}
      </p>
    </div>
  );
}
